import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../cn";

export interface DropdownItem {
  id: string;
  label: ReactNode;
  onSelect: () => void;
  tone?: "default" | "danger";
  separatorBefore?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: "start" | "end";
  className?: string;
  menuTitle?: string;
  selectedId?: string;
  inline?: boolean;
  /**
   * The trigger's accessible name, when its own contents do not read as one.
   * A chip whose visible text is just the current value ("Claude Sonnet 4.6")
   * needs to announce what that value is *of* — otherwise a screen reader
   * hears a model name and no clue that it is a control, let alone which one.
   */
  label?: string;
  /**
   * Which side the menu opens on. "below" is right almost everywhere; "above"
   * exists for a trigger that sits at the bottom of a screen — a composer's
   * own footer — where a menu opening downwards is cut off by the viewport and
   * cannot be scrolled to, because the page beneath it has already ended.
   */
  placement?: "below" | "above";
  /** Classes merged last into the menu, for a width the default does not give it. */
  menuClassName?: string;
}

/**
 * A menu of actions. Opening focuses the selected item, or the first. Arrow
 * keys move between items and wrap, Home and End jump, Escape closes and
 * returns focus to the trigger, Tab closes and moves on from the trigger, choosing
 * an item returns focus to the trigger, and a click outside closes.
 */
export function Dropdown({
  trigger,
  items,
  align = "start",
  className,
  menuTitle,
  selectedId,
  inline = false,
  label,
  placement = "below",
  menuClassName,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  /**
   * Where the menu goes, measured against the viewport rather than the trigger.
   *
   * The menu used to be `absolute` inside this wrapper, which put it inside
   * whatever the trigger was inside. On the team screen that is a row in a
   * `RowList`, and a `RowList` clips its children to the card's corners — so a
   * menu opened from the last row was cut off at the card's bottom edge.
   * Measured: the card ended at 327 and the menu ran to 387, so "Change role"
   * was drawn entirely outside it. The item was still there, still had a box
   * and still answered to a query; it just could not be seen or clicked, and a
   * click aimed at it landed on the paragraph underneath the list.
   *
   * A menu is not part of the thing it was opened from, so it is not laid out
   * inside it. It is portalled to the body, or into the open modal dialog the
   * trigger sits in, which would otherwise cover it, and placed here.
   */
  const place = useCallback(() => {
    if (inline) return;
    const t = ref.current?.getBoundingClientRect();
    const m = menuRef.current?.getBoundingClientRect();
    if (!t) return;
    const gap = 6;
    const margin = 8;
    const w = m?.width ?? 208;
    const h = m?.height ?? 0;

    let top = placement === "above" ? t.top - h - gap : t.bottom + gap;
    // If it will not fit the way it was asked to open, it opens the other way
    // rather than off the bottom of the window, where there is nothing left to
    // scroll to and the menu is simply gone.
    if (
      placement !== "above" &&
      h &&
      top + h > window.innerHeight - margin &&
      t.top - h - gap > margin
    )
      top = t.top - h - gap;
    // `h &&` for the same reason the branch above has it: until the menu has
    // been laid out its height is zero, and a zero-height menu "fits" anywhere
    // and appears to start above the top of the window from any trigger.
    // Without the guard an explicit `above` was flipped straight back to below
    // on the first pass and never flipped again, so the composer's chip --
    // which sits at the bottom of the screen and is the whole reason `above`
    // exists -- opened downwards off the end of the page.
    if (placement === "above" && h && top < margin) top = t.bottom + gap;

    let left = align === "end" ? t.right - w : t.left;
    left = Math.min(Math.max(left, margin), Math.max(margin, window.innerWidth - w - margin));
    // A dialog that keeps a transform from its entrance animation is the
    // containing block of a fixed child, so the menu's coordinates are its own.
    const host = ref.current?.closest("dialog");
    const origin =
      host && getComputedStyle(host).transform !== "none" ? host.getBoundingClientRect() : null;
    setAt({
      top: Math.round(top - (origin?.top ?? 0) - (origin ? (host?.clientTop ?? 0) : 0)),
      left: Math.round(left - (origin?.left ?? 0) - (origin ? (host?.clientLeft ?? 0) : 0)),
    });
  }, [align, inline, placement]);

  // Before paint, so the menu never shows at 0,0 for a frame. Runs twice: once
  // with the fallback width, once with the real one, because the height is not
  // knowable until it has been laid out.
  useLayoutEffect(() => {
    if (!open || inline) {
      setAt(null);
      return;
    }
    place();
  }, [inline, open, place]);

  const placed = inline || at !== null;
  useEffect(() => {
    if (!open || !placed) return;
    const entries = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    const current = menuRef.current?.querySelector<HTMLElement>('[aria-current="true"]');
    (current ?? entries?.[0])?.focus();
  }, [open, placed]);

  useEffect(() => {
    if (!open) return;
    if (!inline) place();
    // `true` to catch scrolling in any ancestor, not just the window — the
    // trigger may well be inside a scrolling panel.
    if (!inline) {
      window.addEventListener("scroll", place, true);
      window.addEventListener("resize", place);
    }
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [inline, open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      // The menu is no longer a descendant of the wrapper, so "outside" has to
      // mean outside both of them — otherwise the first pointerdown on a menu
      // item closes the menu and the click never lands.
      if (ref.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only the menu closes: a modal dialog around it would otherwise take the
      // same keypress as its own close request.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  function onMenuKey(e: ReactKeyboardEvent) {
    if (e.key === "Tab") {
      triggerRef.current?.focus();
      setOpen(false);
      return;
    }
    const entries = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (entries.length === 0) return;
    const from = entries.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === "ArrowDown"
        ? (from + 1) % entries.length
        : e.key === "ArrowUp"
          ? (Math.max(from, 0) - 1 + entries.length) % entries.length
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? entries.length - 1
              : -1;
    if (next < 0) return;
    e.preventDefault();
    entries[next]?.focus();
  }

  const menu = open ? (
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      onKeyDown={onMenuKey}
      style={inline ? undefined : { top: at?.top ?? 0, left: at?.left ?? 0 }}
      className={cn(
        "z-40 max-h-[min(70dvh,28rem)] max-w-[calc(100vw-1rem)] min-w-44 overflow-y-auto rounded-[var(--radius-control)] border border-rule-strong bg-surface p-1",
        inline ? "relative mx-auto mt-1.5 w-[calc(100%-0.5rem)] min-w-0" : "fixed glass-enter",
        !inline && !at && "invisible",
        menuClassName,
      )}
    >
      {menuTitle ? (
        <p
          role="presentation"
          className="px-2 pt-1 pb-1.5 text-micro font-medium tracking-[0.08em] text-ink-3 uppercase"
        >
          {menuTitle}
        </p>
      ) : null}
      {items.map((item) => (
        // role="none" because a menu owns its menuitems directly. A bare div
        // between the two breaks that relationship, and a screen reader loses
        // the count it reads out — "3 of 6". The wrapper exists only to carry
        // the separator rule.
        <div
          key={item.id}
          role="none"
          className={item.separatorBefore ? "mt-1 border-t border-rule pt-1" : undefined}
        >
          {/* Still `menuitem` with `aria-current`, and not `menuitemradio` with
              `aria-checked`, which is what a single-selection menu should
              strictly be. Switching it broke every caller: the studio and
              environment switchers, the walkthrough, and a dozen journeys all
              find their options with `getByRole("menuitem")`, and a role is
              part of a component's contract however internal it looks. Worth
              doing as its own change, with those call sites moved in the same
              commit — not as a rider on an accessibility fix. */}
          <button
            role="menuitem"
            aria-current={selectedId === item.id ? "true" : undefined}
            onClick={() => {
              triggerRef.current?.focus();
              setOpen(false);
              item.onSelect();
            }}
            className={cn(
              "flex min-h-8 w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left font-sans text-meta transition-colors hover:bg-surface-2/70",
              "focus-visible:bg-surface-2 focus-visible:outline-none",
              item.tone === "danger" ? "text-critical-text" : "text-ink",
              selectedId === item.id && "bg-surface-2/55 font-medium",
            )}
          >
            <span className="min-w-0 truncate">{item.label}</span>
            {selectedId === item.id ? <SelectedIcon /> : null}
          </button>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div ref={ref} className={cn("relative inline-block", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="inline-flex max-w-full items-center align-middle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {trigger}
      </button>
      {inline
        ? menu
        : open && typeof document !== "undefined"
          ? createPortal(menu, triggerRef.current?.closest("dialog") ?? document.body)
          : null}
    </div>
  );
}

function SelectedIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-4 shrink-0 text-accent-text"
      fill="none"
    >
      <path
        d="m3 8 3 3 7-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
